import { ChartFrame, LineLayer, ZeroLine } from '@finsim/charts';
import { timeAxis, type Item, type ScenarioId } from '@finsim/model';
import { useUnit } from 'effector-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { $impact, $result } from '../../../entities/simulation';
import { $valueMode } from '../../../entities/view-settings';
import { cn } from '../../../shared/lib/cn';
import { formatMoney, formatMonthYear, formatYear } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import { Section } from '../../../shared/ui';

/** The item's own series (cash flow or balance) and what it contributes to its variant. */
export function ItemInsights({
  item,
  scenarioId,
  currency,
}: {
  item: Item;
  scenarioId: ScenarioId;
  currency: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [result, impact, valueMode] = useUnit([$result, $impact, $valueMode]);
  const series = result?.scenarios[scenarioId]?.items[item.id];
  const dates = useMemo(
    () => (result ? timeAxis(result.startYearMonth, result.months) : []),
    [result],
  );
  if (!result || !series) return null;

  const values = series.type === 'flow' ? series.cash[valueMode] : series.balance[valueMode];
  const money = (v: number, signed = false): string =>
    formatMoney(v, locale, { currency, signed, compact: Math.abs(v) >= 1e6 }).replace('-', '−');
  const current = impact?.itemId === item.id && impact.scenarioId === scenarioId ? impact : null;
  const lastYear = dates.at(-1) ? formatYear(dates.at(-1) as Date, locale) : '';

  return (
    <Section title={t('inspector.sections.insights')}>
      <div className="-mx-1">
        <ChartFrame
          dates={dates}
          height={110}
          domainSeries={[values]}
          formatY={(v) => money(v)}
          formatXTick={(d) => formatYear(d, locale)}
          margin={{ top: 6, right: 6, bottom: 22 }}
          ariaLabel={t('inspector.miniChartAria', { name: item.name })}
          renderTooltip={(i) => (
            <p className="flex justify-between gap-3">
              <span className="text-text-3">
                {dates[i] ? formatMonthYear(dates[i], locale) : ''}
              </span>
              <span className="tabular font-medium">{money(values[i] ?? 0)}</span>
            </p>
          )}
        >
          <ZeroLine />
          <LineLayer values={values} color="var(--chart-series-1)" width={1.5} />
        </ChartFrame>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-xs">
        {(
          [
            ['impactNetWorth', current?.impact ? current.impact.finalNetWorth[valueMode] : null],
            ['impactMinCash', current?.impact ? current.impact.minCash : null],
          ] as const
        ).map(([key, value]) => (
          <div key={key} className="rounded-md bg-bg-2 px-2.5 py-2">
            <dt className="text-text-3">{t(`inspector.${key}`, { year: lastYear })}</dt>
            <dd
              className={cn(
                'tabular mt-0.5 text-sm font-medium',
                value !== null && value < 0 && 'text-negative',
              )}
            >
              {value === null ? t('status.calculating') : money(value, true)}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-text-3">{t('inspector.impactHint')}</p>
    </Section>
  );
}
